import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, RefreshCw, Trash2 } from 'lucide-react';
import InfoTooltip from './onboarding/InfoTooltip';
import {
  connectAppleSearchAds,
  disconnectAppleSearchAds,
  getAppleSearchAdsDashboardSummary,
  triggerAppleSearchAdsSync,
  type AppleSearchAdsDashboardSummary,
} from '../api/client';
import { AppError } from '../api/request';
import { useError } from '../context/ErrorContext';
import ConfirmDialog from './ConfirmDialog';

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

// Apple Search Ads bağlantısı hesap (kullanıcı) seviyesindedir: ASA API credentials Search Ads org
// seviyesinde verilir, tek bir Connect o hesaptaki TÜM uygulamaların kampanyalarını görebilir.
export default function AppleSearchAdsAccountPage() {
  const { t } = useTranslation();
  const { showError } = useError();

  const [summary, setSummary] = useState<AppleSearchAdsDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const [clientId, setClientId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [orgId, setOrgId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getAppleSearchAdsDashboardSummary();
      setSummary(s);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.loadError'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const handleConnect = async () => {
    if (!clientId.trim() || !teamId.trim() || !keyId.trim() || !privateKey.trim()) return;
    setConnecting(true);
    try {
      await connectAppleSearchAds({
        clientId: clientId.trim(),
        teamId: teamId.trim(),
        keyId: keyId.trim(),
        privateKey: privateKey.trim(),
        orgId: orgId.trim() || undefined,
      });
      setClientId('');
      setTeamId('');
      setKeyId('');
      setPrivateKey('');
      setOrgId('');
      await loadSummary();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.connectError'));
    } finally {
      setConnecting(false);
    }
  };

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectAppleSearchAds();
      setDisconnectConfirmOpen(false);
      await loadSummary();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.disconnectError'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await triggerAppleSearchAdsSync();
      await loadSummary();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('appleSearchAds.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('appleSearchAds.pageTitle')}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{t('appleSearchAds.accountPageSubtitle')}</p>
      </div>

      {loading && !summary && <div className="text-center text-gray-500 text-sm py-8">{t('common.loading')}</div>}

      {summary && !summary.connected && (
        <div className="max-w-xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <Megaphone className="h-10 w-10 text-primary-600 dark:text-primary-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('appleSearchAds.heading')}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('appleSearchAds.description')}</p>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appleSearchAds.clientIdLabel')} <span className="text-red-500">*</span>
                <InfoTooltip text={t('onboarding.appleSearchAds.clientIdHelp')} />
              </label>
              <input
                type="text"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="SEARCHADS.xxxxxxxx"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appleSearchAds.teamIdLabel')} <span className="text-red-500">*</span>
                <InfoTooltip text={t('onboarding.appleSearchAds.teamIdHelp')} />
              </label>
              <input
                type="text"
                value={teamId}
                onChange={e => setTeamId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="SEARCHADS.xxxxxxxx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appleSearchAds.keyIdLabel')} <span className="text-red-500">*</span>
                <InfoTooltip text={t('onboarding.appleSearchAds.keyIdHelp')} />
              </label>
              <input
                type="text"
                value={keyId}
                onChange={e => setKeyId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appleSearchAds.privateKeyLabel')} <span className="text-red-500">*</span>
                <InfoTooltip text={t('onboarding.appleSearchAds.privateKeyHelp')} />
              </label>
              <textarea
                value={privateKey}
                onChange={e => setPrivateKey(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                placeholder="-----BEGIN EC PRIVATE KEY-----&#10;...&#10;-----END EC PRIVATE KEY-----"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appleSearchAds.orgIdLabel')}
                <InfoTooltip text={t('onboarding.appleSearchAds.orgIdHelp')} />
              </label>
              <input
                type="text"
                value={orgId}
                onChange={e => setOrgId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="1234567"
              />
            </div>
            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={connecting || !clientId.trim() || !teamId.trim() || !keyId.trim() || !privateKey.trim()}
              className="inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2.5 text-sm font-semibold shadow-sm disabled:opacity-50"
            >
              {connecting ? t('common.loading') : t('appleSearchAds.connect')}
            </button>
          </div>
        </div>
      )}

      {summary && summary.connected && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('appleSearchAds.rangeLabel', { days: 30 })}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleSyncNow()}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? t('common.loading') : t('appleSearchAds.syncNow')}
              </button>
              <button
                type="button"
                onClick={() => setDisconnectConfirmOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-900/50 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('appleSearchAds.disconnect')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label={t('appleSearchAds.totalSpend')} value={summary.totalSpend.toFixed(2)} tooltip={t('onboarding.appleSearchAds.spendHelp')} />
            <StatTile label={t('appleSearchAds.totalInstalls')} value={String(summary.totalInstalls)} tooltip={t('onboarding.appleSearchAds.installsHelp')} />
            <StatTile label={t('appleSearchAds.avgCPA')} value={summary.avgCPA.toFixed(2)} tooltip={t('onboarding.appleSearchAds.cpaHelp')} />
            <StatTile label={t('appleSearchAds.avgTTR')} value={formatPercent(summary.avgTTR)} tooltip={t('onboarding.appleSearchAds.ttrHelp')} />
            <StatTile label={t('appleSearchAds.avgCR')} value={formatPercent(summary.avgCR)} tooltip={t('onboarding.appleSearchAds.crHelp')} />
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('appleSearchAds.appsTitle')}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('appleSearchAds.appsSubtitle')}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2 font-medium">{t('appleSearchAds.colApp')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colInstalls')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('appleSearchAds.colSpend')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(summary.apps ?? []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {t('appleSearchAds.noData')}
                      </td>
                    </tr>
                  )}
                  {(summary.apps ?? []).map(a => (
                    <tr key={a.adamId || a.applicationName}>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">
                        {a.applicationName || t('appleSearchAds.unmatchedApp')}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{a.installs}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{a.spend.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={disconnectConfirmOpen}
        onClose={() => {
          if (!disconnecting) setDisconnectConfirmOpen(false);
        }}
        title={t('appleSearchAds.disconnectTitle')}
        message={t('appleSearchAds.disconnectConfirm')}
        confirmLabel={disconnecting ? t('common.loading') : t('appleSearchAds.disconnect')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDisconnect()}
        confirmLoading={disconnecting}
        closeDisabled={disconnecting}
        variant="danger"
      />
    </div>
  );
}

function StatTile({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="text-lg font-semibold text-gray-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}
